/** RaiderMapCR - Admin panel for classes, restaurants, directory, events, and rooms */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  Snackbar,
  MenuItem,
  CircularProgress,
  Rating,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Restaurant as RestaurantIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  Map as MapIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import AdminRoomsTab from './AdminRoomsTab';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ADMIN_PIN = '0911';

const AdminPage = () => {
  // --- PIN gate state (must be declared before any early return) ---
  const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem('adminAuth') === 'true');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [shake, setShake] = useState(false);

  // --- Admin panel state ---
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data states
  const [classes, setClasses] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [events, setEvents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentDataType, setCurrentDataType] = useState('classes');
  const [formData, setFormData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [directoryCsvFile, setDirectoryCsvFile] = useState(null);
  const [directoryUploading, setDirectoryUploading] = useState(false);

  // showSnackbar defined early so fetchData can reference it
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchData = useCallback(async (type) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/${type}`);
      const data = await response.json();
      const safeData = Array.isArray(data) ? data : [];

      switch (type) {
        case 'classes':
          setClasses(safeData);
          break;
        case 'restaurants':
          setRestaurants(safeData);
          break;
        case 'directory':
          setDirectory(safeData);
          break;
        case 'events':
          setEvents(safeData);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      switch (type) {
        case 'classes':
          setClasses([]);
          break;
        case 'restaurants':
          setRestaurants([]);
          break;
        case 'directory':
          setDirectory([]);
          break;
        case 'events':
          setEvents([]);
          break;
        default:
          break;
      }
    }
    setLoading(false);
  }, []);

  const convertTo12Hour = (time24) => {
    if (!time24) return '';

    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;

    return `${hour12}:${minutes} ${ampm}`;
  };

  const convertTo24Hour = (time12) => {
    if (!time12) return '';

    const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return time12; // Return as-is if doesn't match expected format

    let [, hours, minutes, ampm] = match;
    hours = parseInt(hours, 10);

    if (ampm.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  const saveItem = async () => {
    const method = editingItem ? 'PUT' : 'POST';
    const url = editingItem
      ? `/api/admin/${currentDataType}?id=${editingItem.id}`
      : `/api/admin/${currentDataType}`;
    let dataToSend = { ...formData };

    if (currentDataType === 'classes') {
      if (!formData.course_name || !formData.course_prefix || !formData.course_number) {
        showSnackbar('Course name, prefix, and number are required', 'error');
        return;
      }
      if (formData.start_time) {
        dataToSend.start_time = convertTo12Hour(formData.start_time);
      }
      if (formData.end_time) {
        dataToSend.end_time = convertTo12Hour(formData.end_time);
      }
      if (formData.modality) {
        dataToSend.modality = formData.modality.toLowerCase() === 'face to face' ? 'face to face' : 'videoconference';
      }
      if (formData.type) {
        dataToSend.type = formData.type.charAt(0).toUpperCase() + formData.type.slice(1).toLowerCase();
      }
    }

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();

      if (response.ok) {
        showSnackbar(result.message, 'success');
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({});
        fetchData(currentDataType);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      console.error('Error saving item:', error);
      showSnackbar('Error saving item', 'error');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const url = `/api/admin/${currentDataType}?id=${id}`;

      const response = await fetch(url, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (response.ok) {
        showSnackbar(result.message, 'success');
        fetchData(currentDataType);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showSnackbar('Error deleting item', 'error');
    }
  };

  const openEditDialog = (item = null) => {
    setEditingItem(item);
    if (item && currentDataType === 'classes') {
      let coursePrefix = item.course_prefix || '';
      let courseNumber = item.course_number || '';

      if (item.course_code && !item.course_prefix && !item.course_number) {
        const courseCodeParts = item.course_code.split(' ');
        coursePrefix = courseCodeParts[0] || '';
        courseNumber = courseCodeParts[1] || '';
      }

      const formDataWithSplit = {
        ...item,
        course_prefix: coursePrefix,
        course_number: courseNumber,
        days: item.days || '',
        type: item.type || 'Lecture',
        modality: item.modality || 'face to face',
        start_time: item.start_time ? convertTo24Hour(item.start_time) : '',
        end_time: item.end_time ? convertTo24Hour(item.end_time) : ''
      };
      setFormData(formDataWithSplit);
    } else {
      const formDataForEdit = item ? { ...item } : {};
      setFormData(formDataForEdit);
    }

    setOpenDialog(true);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    switch (newValue) {
      case 0: setCurrentDataType('classes'); break;
      case 1: setCurrentDataType('restaurants'); break;
      case 2: setCurrentDataType('directory'); break;
      case 3: setCurrentDataType('events'); break;
      case 4: setCurrentDataType('rooms'); break;
      default: setCurrentDataType('classes');
    }
  };

  const handleCalculateOccupancy = async () => {
    // Prevent double-clicking
    if (loading) return;

    setLoading(true);
    try {
      const response = await fetch('/api/admin/calculate-occupancy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const result = await response.json();

      if (response.ok) {
        showSnackbar(
          `Occupancy calculation completed! Processed ${result.processedClasses} classes and created ${result.totalEntries} entries.`,
          'success'
        );
        if (result.errors > 0) {
          showSnackbar(`${result.errors} errors occurred during processing.`, 'warning');
        }
      } else {
        showSnackbar(result.error || 'Failed to calculate occupancy', 'error');
      }
    } catch (error) {
      console.error('Error calculating occupancy:', error);
      showSnackbar('Error calculating occupancy', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      showSnackbar('Please select a CSV file', 'error');
      return;
    }

    if (!window.confirm('This will replace ALL existing schedules with the CSV data. This action cannot be undone. Continue?')) {
      return;
    }

    setUploading(true);
    try {
      const csvContent = await csvFile.text();

      const response = await fetch('/api/admin/classes/upload-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csvContent })
      });

      const result = await response.json();

      if (response.ok) {
        showSnackbar(
          `All schedules replaced! ${result.successful} schedules imported successfully. ${result.errors} errors.`,
          result.errors > 0 ? 'warning' : 'success'
        );
        fetchData(currentDataType);
        setCsvFile(null);
      } else {
        showSnackbar(result.error || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      showSnackbar('Error uploading CSV file', 'error');
    }

    setUploading(false);
  };

  const handleDirectoryCsvUpload = async () => {
    if (!directoryCsvFile) {
      showSnackbar('Please select a CSV file', 'error');
      return;
    }

    if (!window.confirm('This will replace ALL existing directory entries with the CSV data. This action cannot be undone. Continue?')) {
      return;
    }

    setDirectoryUploading(true);
    try {
      const csvContent = await directoryCsvFile.text();

      const response = await fetch('/api/admin/directory/upload-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csvContent })
      });

      const result = await response.json();

      if (response.ok) {
        showSnackbar(
          `All directory entries replaced! ${result.successful} entries imported successfully. ${result.errors} errors.`,
          result.errors > 0 ? 'warning' : 'success'
        );
        fetchData(currentDataType);
        setDirectoryCsvFile(null);
      } else {
        showSnackbar(result.error || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      showSnackbar('Error uploading CSV file', 'error');
    }

    setDirectoryUploading(false);
  };


  useEffect(() => {
    fetchData('classes');
  }, [fetchData]);

  useEffect(() => {
    if (currentDataType && tabValue >= 0 && currentDataType !== 'rooms') {
      fetchData(currentDataType);
    }
  }, [currentDataType, tabValue, fetchData]);

  // --- PIN gate handlers & early return (after all hooks) ---
  const handlePinInput = (digit) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);
    if (newPin.length === 4) {
      if (newPin === ADMIN_PIN) {
        sessionStorage.setItem('adminAuth', 'true');
        setAuthenticated(true);
        setPinError(false);
      } else {
        setPinError(true);
        setShake(true);
        setTimeout(() => { setPin(''); setPinError(false); setShake(false); }, 700);
      }
    }
  };

  const handlePinBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  if (!authenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0f0f0f 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Inter", "Poppins", sans-serif'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '24px',
          padding: '48px 40px',
          width: '320px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          backdropFilter: 'blur(20px)',
          textAlign: 'center',
          animation: shake ? 'shake 0.4s ease' : 'none'
        }}>
          <style>{`
            @keyframes shake {
              0%, 100% { transform: translateX(0); }
              15% { transform: translateX(-8px); }
              30% { transform: translateX(8px); }
              45% { transform: translateX(-6px); }
              60% { transform: translateX(6px); }
              75% { transform: translateX(-3px); }
              90% { transform: translateX(3px); }
            }
          `}</style>
          <h2 style={{ color: '#ffffff', margin: '0 0 32px', fontWeight: 700, fontSize: '22px' }}>Admin Access</h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginBottom: '32px' }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{
                width: '16px', height: '16px', borderRadius: '50%',
                background: i < pin.length ? (pinError ? '#f44336' : '#AA3333') : 'rgba(255,255,255,0.15)',
                transition: 'background 0.2s ease',
                boxShadow: i < pin.length && !pinError ? '0 0 8px rgba(170,51,51,0.6)' : 'none'
              }} />
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
              <button key={d} id={`pin-btn-${d}`} onClick={() => handlePinInput(String(d))} style={{
                background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px', color: '#ffffff', fontSize: '20px', fontWeight: 600,
                height: '56px', cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: 'inherit'
              }}
                onMouseEnter={e => { e.target.style.background = 'rgba(170,51,51,0.25)'; e.target.style.borderColor = 'rgba(170,51,51,0.5)'; }}
                onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.07)'; e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}>
                {d}
              </button>
            ))}
            <button id="pin-btn-backspace" onClick={handlePinBackspace} style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px', color: 'rgba(255,255,255,0.5)', fontSize: '18px',
              height: '56px', cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: 'inherit'
            }}
              onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.05)'; }}>
              ⌫
            </button>
            <button id="pin-btn-0" onClick={() => handlePinInput('0')} style={{
              background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px', color: '#ffffff', fontSize: '20px', fontWeight: 600,
              height: '56px', cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: 'inherit'
            }}
              onMouseEnter={e => { e.target.style.background = 'rgba(170,51,51,0.25)'; e.target.style.borderColor = 'rgba(170,51,51,0.5)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.07)'; e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}>
              0
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  const getFormFields = () => {
    switch (currentDataType) {
      case 'classes':
        return [
          { name: 'course_name', label: 'Course Name', required: true },
          { name: 'course_prefix', label: 'Course Prefix (e.g., CS)', required: true },
          { name: 'course_number', label: 'Course Number (e.g., 1301)', required: true, type: 'number' },
          {
            name: 'type', label: 'Class Type', type: 'select', required: true,
            options: ['Lecture', 'Lab', 'Discussion']
          },
          {
            name: 'modality', label: 'Modality', type: 'select', required: true,
            options: ['face to face', 'videoconference']
          },
          { name: 'start_time', label: 'Start Time', type: 'time' },
          { name: 'end_time', label: 'End Time', type: 'time' },
          {
            name: 'days', label: 'Days', type: 'chips',
            options: ['M', 'T', 'W', 'R', 'F']
          },
          { name: 'room', label: 'Room' },
          { name: 'professor', label: 'Professor' }
        ];
      case 'restaurants':
        return [
          { name: 'name', label: 'Restaurant Name', required: true },
          { name: 'cuisine', label: 'Cuisine Type' },
          { name: 'description', label: 'Description' },
          {
            name: 'location',
            label: 'Location',
            type: 'select',
            required: true,
            options: ['In Avenida Escazú', 'Delivery']
          },
          { name: 'hours', label: 'Operating Hours' },
          {
            name: 'price_range',
            label: 'Price Range',
            type: 'select',
            options: ['$', '$$', '$$$', '$$$$']
          },
          { name: 'rating', label: 'Rating', type: 'number', min: 0, max: 5, step: 0.1 }
        ];
      case 'directory':
        return [
          { name: 'name', label: 'Full Name', required: true },
          { name: 'title', label: 'Job Title' },
          { name: 'department', label: 'Department' },
          { name: 'email', label: 'Email Address', type: 'email' },
          { name: 'phone', label: 'Phone Number', type: 'tel' },
          { name: 'office', label: 'Office Location' },
          { name: 'hours', label: 'Office Hours' }
        ];
      case 'events':
        return [
          { name: 'title', label: 'Event Title', required: true },
          { name: 'description', label: 'Description' },
          { name: 'date', label: 'Date', type: 'date' },
          { name: 'time', label: 'Time' },
          { name: 'location', label: 'Location' },
          { name: 'organizer', label: 'Organizer' },
          { name: 'google_event_id', label: 'Google Event ID' }
        ];
      default:
        return [];
    }
  };

  const getTableColumns = () => {
    switch (currentDataType) {
      case 'classes':
        return ['Course Code', 'Course Name', 'Type', 'Modality', 'Time', 'Days', 'Room', 'Professor', 'Actions'];
      case 'restaurants':
        return ['Name', 'Cuisine', 'Location', 'Price Range', 'Rating', 'Hours', 'Actions'];
      case 'directory':
        return ['Name', 'Title', 'Department', 'Email', 'Office', 'Phone', 'Actions'];
      case 'events':
        return ['Title', 'Date', 'Time', 'Location', 'Organizer', 'Actions'];
      default:
        return [];
    }
  };

  const renderTableRow = (item) => {
    switch (currentDataType) {
      case 'classes':
        return (
          <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <Chip label={item.course_code} size="small" sx={{ backgroundColor: '#AA3333', color: '#ffffff' }} />
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.course_name}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <Chip label={item.type} size="small" sx={{ backgroundColor: '#AA3333', color: '#ffffff' }} />
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <Chip label={item.modality} size="small" sx={{ backgroundColor: 'rgba(74, 74, 74, 0.6)', color: '#ffffff' }} />
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              {item.start_time && item.end_time ? `${item.start_time} - ${item.end_time}` : item.start_time || 'TBD'}
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <Chip label={item.days} size="small" sx={{ backgroundColor: 'rgba(74, 74, 74, 0.6)', color: '#ffffff' }} />
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.room}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.professor}</TableCell>
            <TableCell sx={{ borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <IconButton onClick={() => openEditDialog(item)} sx={{ color: '#AA3333' }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => deleteItem(item.id)} sx={{ color: '#f44336' }}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      case 'restaurants':
        return (
          <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.name}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <Chip label={item.cuisine} size="small" sx={{ backgroundColor: 'rgba(74, 74, 74, 0.6)', color: '#ffffff' }} />
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <Chip label={item.location} size="small" sx={{ backgroundColor: 'rgba(74, 74, 74, 0.6)', color: '#ffffff' }} />
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.price_range}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <Rating value={parseFloat(item.rating) || 0} readOnly size="small" sx={{ '& .MuiRating-iconFilled': { color: '#AA3333' } }} />
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.hours}</TableCell>
            <TableCell sx={{ borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <IconButton onClick={() => openEditDialog(item)} sx={{ color: '#AA3333' }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => deleteItem(item.id)} sx={{ color: '#f44336' }}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      case 'directory':
        return (
          <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.name}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.title || '-'}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              {item.department ? (
                <Chip label={item.department} size="small" sx={{ backgroundColor: 'rgba(74, 74, 74, 0.6)', color: '#ffffff' }} />
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.email || '-'}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.office || '-'}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.phone || '-'}</TableCell>
            <TableCell sx={{ borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <IconButton onClick={() => openEditDialog(item)} sx={{ color: '#AA3333' }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => deleteItem(item.id)} sx={{ color: '#f44336' }}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      case 'events':
        return (
          <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.title}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.date}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.time}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.location}</TableCell>
            <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>{item.organizer}</TableCell>
            <TableCell sx={{ borderColor: 'rgba(74, 74, 74, 0.6)' }}>
              <IconButton onClick={() => openEditDialog(item)} sx={{ color: '#AA3333' }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => deleteItem(item.id)} sx={{ color: '#f44336' }}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      default:
        return null;
    }
  };

  const getCurrentData = () => {
    let data;
    switch (currentDataType) {
      case 'classes':
        data = classes;
        break;
      case 'restaurants':
        data = restaurants;
        break;
      case 'directory':
        data = directory;
        break;
      case 'events':
        data = events;
        break;
      default:
        data = [];
    }
    return Array.isArray(data) ? data : [];
  };

  return (
    <div className="main-content" style={{
      background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)',
      minHeight: '100vh',
      color: '#ffffff'
    }}>
      <style jsx>{`
        /* Dark theme for native browser elements */
        input[type="time"]::-webkit-calendar-picker-indicator,
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
          cursor: pointer;
        }
        
        input[type="time"]::-webkit-datetime-edit-text,
        input[type="date"]::-webkit-datetime-edit-text {
          color: #ffffff !important;
        }
        
        input[type="time"]::-webkit-datetime-edit-fields-wrapper,
        input[type="date"]::-webkit-datetime-edit-fields-wrapper {
          background-color: transparent;
        }
        
        input[type="time"]::-webkit-datetime-edit-ampm-field,
        input[type="date"]::-webkit-datetime-edit-month-field,
        input[type="date"]::-webkit-datetime-edit-day-field,
        input[type="date"]::-webkit-datetime-edit-year-field {
          color: #ffffff !important;
        }
        
        /* Material-UI Select dropdown styling */
        .MuiSelect-icon {
          color: #ffffff !important;
        }
        
        .MuiInputAdornment-root {
          color: #ffffff !important;
        }
        
        .MuiSvgIcon-root {
          color: #ffffff !important;
        }
        
        /* ToggleButton text and icons */
        .MuiToggleButton-root {
          color: #ffffff !important;
        }
        
        .MuiToggleButton-root .MuiSvgIcon-root {
          color: #ffffff !important;
        }
      `}</style>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 4, color: '#ffffff' }}>
          <DashboardIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          Admin Panel
        </Typography>

        <Box sx={{
          borderBottom: 1,
          borderColor: 'rgba(74, 74, 74, 0.6)',
          mb: 3,
          '& .MuiTabs-root': {
            '& .MuiTab-root': {
              color: '#e0e0e0',
              '&.Mui-selected': {
                color: '#AA3333'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#AA3333'
            }
          }
        }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab icon={<SchoolIcon />} label="Classes" />
            <Tab icon={<RestaurantIcon />} label="Restaurants" />
            <Tab icon={<PeopleIcon />} label="Directory" />
            <Tab icon={<EventIcon />} label="Events" />
            <Tab icon={<MapIcon />} label="Rooms" />
          </Tabs>
        </Box>

        {[0, 1, 2, 3].map((tabIndex) => (
          <TabPanel key={tabIndex} value={tabValue} index={tabIndex}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                Manage {currentDataType.charAt(0).toUpperCase() + currentDataType.slice(1)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {currentDataType === 'classes' && (
                  <>
                    <input
                      accept=".csv"
                      style={{ display: 'none' }}
                      id="csv-file-input"
                      type="file"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                    />
                    <label htmlFor="csv-file-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={uploading}
                        sx={{
                          borderRadius: 2,
                          borderColor: 'rgba(74, 74, 74, 0.6)',
                          color: '#ffffff',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        {uploading ? 'Uploading...' : 'Upload CSV'}
                      </Button>
                    </label>
                    {csvFile && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCsvUpload}
                        disabled={uploading}
                        sx={{
                          borderRadius: 2,
                          backgroundColor: '#AA3333',
                          '&:hover': {
                            backgroundColor: '#8B2A2A'
                          }
                        }}
                      >
                        Replace All with {csvFile.name}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCalculateOccupancy}
                      disabled={loading}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: '#AA3333',
                        '&:hover': {
                          backgroundColor: '#8B2A2A'
                        }
                      }}
                    >
                      {loading ? 'Calculating...' : 'Calculate Occupancy'}
                    </Button>
                  </>
                )}
                {currentDataType === 'directory' && (
                  <>
                    <input
                      accept=".csv"
                      style={{ display: 'none' }}
                      id="directory-csv-file-input"
                      type="file"
                      onChange={(e) => setDirectoryCsvFile(e.target.files[0])}
                    />
                    <label htmlFor="directory-csv-file-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={directoryUploading}
                        sx={{
                          borderRadius: 2,
                          borderColor: 'rgba(74, 74, 74, 0.6)',
                          color: '#ffffff',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        {directoryUploading ? 'Uploading...' : 'Upload CSV'}
                      </Button>
                    </label>
                    {directoryCsvFile && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDirectoryCsvUpload}
                        disabled={directoryUploading}
                        sx={{
                          borderRadius: 2,
                          backgroundColor: '#AA3333',
                          '&:hover': {
                            backgroundColor: '#8B2A2A'
                          }
                        }}
                      >
                        Replace All with {directoryCsvFile.name}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openEditDialog()}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: '#AA3333',
                    '&:hover': {
                      backgroundColor: '#8B2A2A'
                    }
                  }}
                >
                  Add New
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#AA3333' }} />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(42, 42, 42, 0.5)',
                border: '1px solid rgba(74, 74, 74, 0.4)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {getTableColumns().map((column) => (
                        <TableCell key={column} sx={{ fontWeight: 'bold', color: '#ffffff', borderColor: 'rgba(74, 74, 74, 0.6)' }}>
                          {column}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getCurrentData().map(renderTableRow)}
                    {getCurrentData().length === 0 && (
                      <TableRow>
                        <TableCell colSpan={getTableColumns().length} sx={{ textAlign: 'center', py: 4, borderColor: 'rgba(74, 74, 74, 0.6)' }}>
                          <Typography variant="body1" sx={{ color: '#e0e0e0' }}>
                            No data available. Click "Add New" to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        ))}

        <TabPanel value={tabValue} index={4}>
          <AdminRoomsTab />
        </TabPanel>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(42, 42, 42, 0.5)',
              border: '1px solid rgba(74, 74, 74, 0.4)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              color: '#ffffff'
            }
          }}
        >
          <DialogTitle sx={{ color: '#ffffff' }}>
            {editingItem ? 'Edit' : 'Add New'} {currentDataType.slice(0, -1)}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {getFormFields().map((field, index) => {
                if (currentDataType === 'classes' && field.name === 'course_prefix') {
                  const numberField = getFormFields()[index + 1];
                  return (
                    <Grid container item xs={12} spacing={2} key={field.name}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={field.label}
                          value={formData[field.name] || ''}
                          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                          required={field.required}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#ffffff',
                              '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: '#AA3333' },
                              '& .MuiSelect-icon': { color: '#ffffff' },
                              '& .MuiInputAdornment-root': { color: '#ffffff' },
                              '& input[type="time"]::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                                cursor: 'pointer'
                              },
                              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                                cursor: 'pointer'
                              }
                            },
                            '& .MuiInputLabel-root': { color: '#e0e0e0' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={numberField.label}
                          type={numberField.type || 'text'}
                          value={formData[numberField.name] || ''}
                          onChange={(e) => setFormData({ ...formData, [numberField.name]: e.target.value })}
                          required={numberField.required}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#ffffff',
                              '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: '#AA3333' },
                              '& .MuiSelect-icon': { color: '#ffffff' },
                              '& .MuiInputAdornment-root': { color: '#ffffff' },
                              '& input[type="time"]::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                                cursor: 'pointer'
                              },
                              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                                cursor: 'pointer'
                              }
                            },
                            '& .MuiInputLabel-root': { color: '#e0e0e0' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' }
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                }
                if (currentDataType === 'classes' && field.name === 'course_number') {
                  return null;
                }

                return (
                  <Grid item xs={12} sm={field.name === 'description' ? 12 : (field.type === 'chips' ? 12 : 6)} key={field.name}>
                    {field.type === 'select' ? (
                      <TextField
                        select
                        fullWidth
                        label={field.label}
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        required={field.required}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused fieldset': { borderColor: '#AA3333' }
                          },
                          '& .MuiInputLabel-root': { color: '#e0e0e0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: 'rgba(42, 42, 42, 0.5)',
                              border: '1px solid rgba(74, 74, 74, 0.4)',
                              backdropFilter: 'blur(12px)',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                              '& .MuiMenuItem-root': {
                                color: '#ffffff',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(170, 51, 51, 0.2)',
                                  '&:hover': { backgroundColor: 'rgba(170, 51, 51, 0.3)' }
                                }
                              }
                            }
                          }
                        }}
                      >
                        {field.options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : field.type === 'chips' ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#e0e0e0' }}>
                          {field.label} {field.required && '*'}
                        </Typography>
                        <ToggleButtonGroup
                          value={formData[field.name] ? formData[field.name].split('') : []}
                          onChange={(e, selectedDays) => {
                            // Sort days in MTWRF order instead of alphabetical
                            const dayOrder = ['M', 'T', 'W', 'R', 'F'];
                            const sortedDays = dayOrder.filter(day => selectedDays.includes(day));
                            const daysString = sortedDays.join('');
                            setFormData({ ...formData, [field.name]: daysString });
                          }}
                          size="small"
                          sx={{
                            flexWrap: 'wrap',
                            '& .MuiToggleButton-root': {
                              color: '#ffffff',
                              borderColor: 'rgba(74, 74, 74, 0.6)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderColor: 'rgba(255, 255, 255, 0.3)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#AA3333',
                                color: '#ffffff',
                                borderColor: '#AA3333',
                                '&:hover': {
                                  backgroundColor: '#8B2A2A',
                                  borderColor: '#8B2A2A'
                                }
                              }
                            }
                          }}
                        >
                          {field.options.map((day) => (
                            <ToggleButton key={day} value={day} sx={{ minWidth: 40 }}>
                              {day}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#e0e0e0' }}>
                          Selected: {formData[field.name] || 'None'}
                        </Typography>
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        label={field.label}
                        type={field.type || 'text'}
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        required={field.required}
                        multiline={field.name === 'description'}
                        rows={field.name === 'description' ? 3 : 1}
                        InputLabelProps={{
                          shrink: field.type === 'time' ? true : undefined,
                        }}
                        inputProps={{
                          min: field.min,
                          max: field.max,
                          step: field.step || (field.type === 'time' ? 300 : undefined), // 5 minute intervals for time
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused fieldset': { borderColor: '#AA3333' }
                          },
                          '& .MuiInputLabel-root': { color: '#e0e0e0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' }
                        }}
                      />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: 'rgba(42, 42, 42, 0.3)' }}>
            <Button
              onClick={() => setOpenDialog(false)}
              sx={{
                color: '#e0e0e0',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={saveItem}
              variant="contained"
              sx={{
                backgroundColor: '#AA3333',
                '&:hover': { backgroundColor: '#8B2A2A' }
              }}
            >
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default AdminPage;
